'use client'

import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {Section} from '@/components/common/content/Section'
import {Hero} from '@/components/common/hero/Hero'
import {MostRecentArticles} from '@/components/common/recent-articles/MostRecentArticles'
import {Page} from '@/components/Page'
import {QuestionsAndAnswersRelatedQuestions} from '@/components/questions-and-answers/QuestionsAndAnswersRelatedQuestions'
import {IpCaseDetail} from '@/components/trademark-disputes/case/IpCaseDetail'
import {IpCaseTmComparison} from '@/components/trademark-disputes/case/IpCaseTmComparison'
import {SelectedIpCases} from '@/components/trademark-disputes/home/ip-cases/SelectedIpCases'
import {TrademarkDisputesBreadcrumbs} from '@/components/trademark-disputes/TrademarkDisputesBreadcrumbs'
import {getTrademarkDisputesContent} from '@/content/trademarkDisputes'
import {useTranslation} from '@/i18n'
import {RecentArticleCard} from '@/utils/contentful'
import {QuestionsAndAnswersContentType} from '@/utils/questions-and-answers/constants'
import {QuestionsAndAnswersRelatedQuestion} from '@/utils/questions-and-answers/types'
import {
  TrademarkDisputeCaseDetail,
  TrademarkDisputeSelectedCase,
} from '@/utils/trademark-disputes/types'

type TrademarkDisputeCasePageProps = {
  breadcrumbTrail: BreadcrumbStep[]
  ipCase: TrademarkDisputeCaseDetail
  selectedIpCases: TrademarkDisputeSelectedCase[]
  relatedQuestions: QuestionsAndAnswersRelatedQuestion[]
  relatedArticles: RecentArticleCard[]
}

const TrademarkDisputeCasePage: FC<TrademarkDisputeCasePageProps> = ({
  breadcrumbTrail,
  ipCase,
  selectedIpCases,
  relatedQuestions,
  relatedArticles,
}) => {
  const {t} = useTranslation()
  const {ipCasesFaqTitle, relatedArticlesTitle, relatedCasesTitle} = getTrademarkDisputesContent()

  const {name, comparisonOfTms} = ipCase

  const hasArticles = relatedArticles.length > 0
  const hasQuestions = relatedQuestions.length > 0

  return (
    <Page>
      <Hero
        title={name}
        maxWidth="unset"
        breadcrumbs={<TrademarkDisputesBreadcrumbs trail={breadcrumbTrail} />}
      />
      <Section hasPaddingTop>
        <IpCaseDetail ipCase={ipCase} />
        <IpCaseTmComparison caseName={name} comparisonOfTms={comparisonOfTms} />
      </Section>
      <Section hasPaddingTop hasPaddingBottom={!hasArticles && !hasQuestions}>
        <SelectedIpCases title={t(relatedCasesTitle)} ipCases={selectedIpCases} />
      </Section>
      {hasArticles && (
        <Section hasPaddingTop hasPaddingBottom={!hasQuestions}>
          <MostRecentArticles
            articlesTitle={t(relatedArticlesTitle)}
            articles={relatedArticles}
            trackedEvent={{
              name: 'RelatedBlogs-TrademarkDisputeIpCase',
              parameters: {ipCase: name},
            }}
          />
        </Section>
      )}
      {hasQuestions && (
        <Section hasPaddingTop hasPaddingBottom isHighlighted>
          <QuestionsAndAnswersRelatedQuestions
            type={QuestionsAndAnswersContentType.trademark}
            title={t(ipCasesFaqTitle)}
            relatedQuestions={relatedQuestions}
          />
        </Section>
      )}
    </Page>
  )
}

export default TrademarkDisputeCasePage
